import { h as __h } from "preact";
import { signal } from "@preact/signals";
import { scaleLinear, scaleTime } from "d3-scale";
import { area, curveLinear, line } from "d3-shape";
import { Component } from "preact";
/** @import { ComponentChildren } from "preact"; */

import html from "~/util/html.js";
import Icon from "~/component/Icon.js";
import { timestamp } from "~/component/Time.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./StatsGraph.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./StatsGraph.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @param {object} props
 * @param {number} props.value
 * @param {number | null} [props.prev]
 * @param {"up-bad" | "up-good"} [props.polarity]
 */
export function Trend({
  value,
  prev,
  polarity
}) {
  const v = Math.floor(value);
  let trend = "flat";
  if (prev != null) {
    const p = Math.floor(prev);
    trend = v === p ? "flat" : v > p ? "up" : "down";
  }
  let tone = "flat";
  if (trend !== "flat" && polarity) {
    tone = trend === (polarity === "up-good" ? "up" : "down") ? "good" : "bad";
  }
  return __h("span", {
    "class": css.trend,
    "data-tone": tone
  }, __h(Icon, {
    name: "trend-" + trend,
    size: 16,
    label: "trending " + trend
  }));
}

/**
 * @extends {Component<StatsProps>}
 *
 * @typedef {{ timestamp: string; value: number }} StatsSample
 *
 * @typedef {object} StatsProps
 * @property {string} title
 * @property {(value: number, prev?: number | null) => ComponentChildren} [format]
 * @property {[number, number]} [domain]
 * @property {StatsSample[]} points
 * @property {string} [label]
 * @property {{ label: string; points: StatsSample[] }} [stack] A second series
 */
export class StatsGraph extends Component {
  static DURATION = 3000;
  static MAX_SAMPLES = 100;
  static COLORS = ["var(--color-primary)", "var(--orange-500)"];
  collapsed = signal(false);
  get samples() {
    /** @type {Map<string, number>} */
    const extra = new Map((this.props.stack?.points ?? []).map(p => [p.timestamp, p.value]));
    return this.props.points.map(p => {
      const base = p.value ?? 0;
      const added = extra.get(p.timestamp) ?? 0;
      return {
        time: new Date(p.timestamp).getTime(),
        base,
        added,
        total: base + added
      };
    });
  }
  render() {
    return __h("button", {
      "class": css.container,
      onClick: () => this.collapsed.value = !this.collapsed.value
    }, __h("div", {
      "class": css.head
    }, __h("div", {
      "class": css.icon,
      "data-collapsed": this.collapsed.value
    }, __h(Icon, {
      name: "down-filled",
      size: 16
    })), __h("span", {
      "class": css.title
    }, this.props.title), this.renderBreakdown(), __h("span", {
      "class": css.total
    }, this.renderLatest(s => s.total))), this.renderGraph());
  }
  renderGraph() {
    const {
      stack,
      domain
    } = this.props;
    const samples = this.samples;
    const span = StatsGraph.MAX_SAMPLES * StatsGraph.DURATION;
    const end = samples.at(-1)?.time ?? span;
    const x = scaleTime().domain([new Date(end - span), new Date(end)]).range([0, 100]);
    const y = scaleLinear().range([100, 0]);
    if (domain) y.domain(domain);else y.domain([0, Math.max(1, ...samples.map(s => s.total))]).nice(4);
    const yTicks = y.ticks(4);
    const xTicks = x.ticks(5).filter(tick => x(tick) < 95);
    const lineGen = line().curve(curveLinear);
    /** @type {[number, number][]} */
    const base = samples.map(s => [x(s.time), y(s.base)]);
    const bands = [{
      line: lineGen(base),
      area: area().y0(y(0)).curve(curveLinear)(base)
    }];
    if (stack) {
      /** @type {[number, number][]} */
      const top = samples.map(s => [x(s.time), y(s.total)]);
      bands.push({
        line: lineGen(top),
        area: area().y0((_, i) => base[i]?.[1] ?? y(0)).curve(curveLinear)(top)
      });
    }
    return __h("div", {
      "class": css.graph,
      "data-collapsed": this.collapsed.value
    }, __h("svg", {
      viewBox: "0 0 100 100",
      preserveAspectRatio: "none"
    }, __h("defs", {}, StatsGraph.COLORS.map((color, i) => __h("linearGradient", {
      key: i,
      id: "stats-graph-gradient-" + i,
      gradientUnits: "userSpaceOnUse",
      x1: "0",
      y1: "0",
      x2: "0",
      y2: "100"
    }, __h("stop", {
      offset: "0%",
      "stop-color": color,
      "stop-opacity": "0.5"
    }), __h("stop", {
      offset: "100%",
      "stop-color": color,
      "stop-opacity": "0"
    })))), bands.map((band, i) => __h("path", {
      key: i,
      d: band.area ?? "",
      fill: "url(#stats-graph-gradient-" + i + ")"
    })), bands.map((band, i) => __h("path", {
      key: i,
      d: band.line ?? "",
      fill: "none",
      stroke: StatsGraph.COLORS[i],
      "stroke-width": "1.5",
      "stroke-linejoin": "round",
      "stroke-linecap": "round",
      "vector-effect": "non-scaling-stroke"
    })), yTicks.map(tick => __h("line", {
      key: tick,
      x1: "0",
      x2: "100",
      y1: y(tick),
      y2: y(tick),
      stroke: "var(--color-border)",
      "vector-effect": "non-scaling-stroke"
    })), xTicks.map(tick => __h("line", {
      key: tick.getTime(),
      x1: x(tick),
      x2: x(tick),
      y1: "0",
      y2: "100",
      stroke: "var(--color-border)",
      "vector-effect": "non-scaling-stroke"
    }))), yTicks.filter(y => y !== 0).map(tick => __h("span", {
      key: tick,
      style: "white-space: nowrap; position: absolute; right: calc(100%); padding-right: 6px; top: " + y(tick) + "%; translate: 0 -4px;"
    }, tick)), xTicks.map(tick => __h("span", {
      key: tick.getTime(),
      style: "white-space: nowrap; position: absolute; top: 100%; left: " + x(tick) + "%; translate: -50% 0;"
    }, timestamp.format(tick))));
  }
  renderBreakdown() {
    const {
      stack,
      label,
      title
    } = this.props;
    if (!stack) return null;

    /** @type {{ label: string; pick: (s: StatsGraph["samples"][number]) => number }[]} */
    const series = [{
      label: label ?? title,
      pick: s => s.base
    }, {
      label: stack.label,
      pick: s => s.added
    }];
    return __h("span", {
      "class": css.breakdown
    }, series.map((s, i) => __h("span", {
      "class": css.tag,
      key: i,
      style: "--series-color: " + StatsGraph.COLORS[i] + ";"
    }, __h("span", {
      "class": css.label
    }, s.label), this.renderLatest(s.pick))));
  }

  /**
   * The latest of the picked per-sample values, formatted against the one before it.
   *
   * @param {(s: StatsGraph["samples"][number]) => number} pick
   */
  renderLatest(pick) {
    const values = this.samples.map(pick);
    const value = values.at(-1);
    if (value == null) return "—";
    if (!this.props.format) return value;
    return this.props.format(value, values.at(-2) ?? null);
  }
}